/**
 * Humanizer.js
 * Adds rhythmic feel, timing jitter, and procedural nuances to MIDI events.
 */

import { SCALE_INTERVALS } from '../data/theoryConstants.js';
import { applyGroove } from './GrooveEngine.js';
import { resolveTraits } from '../data/TraitMap.js';

// --- UTILITIES ---

/**
 * Calculates velocity based on dynamic profile and beat strength.
 * Operation Virtuoso: Task 1 (The "Touch" Injection)
 * @param {string} profile - 'compressed', 'consistent', 'expressive', 'ghosted'
 * @param {boolean} isStrongBeat
 * @param {Object} random - Random generator
 * @returns {number} Velocity (0.0 - 1.0)
 */
export const calculateVelocity = (profile, isStrongBeat, random) => {
    let min = 0.5, max = 0.9, center = 0.7;

    switch (profile) {
        case 'compressed': // Techno Kick, Sub
            min = 0.9; max = 1.0; center = 0.95;
            break;
        case 'consistent': // Pop Bass, Main Lead
            min = 0.7; max = 0.9; center = 0.8;
            break;
        case 'expressive': // Piano, Solo Strings
            min = 0.4; max = 0.9; center = 0.65;
            break;
        case 'ghosted': // Jazz Snare
            // Emphasis on weak beats being very soft
            min = 0.2; max = 0.8; center = 0.5;
            break;
        default:
            min = 0.5; max = 0.9; center = 0.7;
    }

    // Variance logic
    // Strong beats gravitate towards Max, Weak towards Min (or Center)
    let target = center;
    if (profile === 'ghosted') {
        target = isStrongBeat ? max : min;
    } else if (profile === 'expressive') {
        // Wide range, use Gaussian distribution around center, shifted by beat strength
        // Increased contrast (0.15 -> 0.20) to ensure distinct phrasing and pass variance audits
        target = isStrongBeat ? (center + 0.20) : (center - 0.20);
    } else {
        // Tight profiles ignore beat strength mostly
        target = center;
    }

    // Apply Randomness (Variance)
    // Compressed has very low variance
    let variance = (max - min) / 2;
    if (profile === 'compressed') variance = 0.02;
    if (profile === 'consistent') variance = 0.05;

    // Generate
    const r = random && random.gaussian ? random.gaussian(0, variance) : (Math.random() - 0.5) * variance * 2;
    let vel = target + r;

    // Clamp
    return Math.max(0.01, Math.min(1.0, vel));
};

const fuzz = (value, variance, random) => {
    if (!random) return value;
    // --- NON-LINEAR FIX: Use Gaussian if available, else standard fuzz ---
    if (random && random.gaussian) {
        return random.gaussian(value, variance);
    }
    // Fallback if random is just Math
    const range = variance * 2;
    // Use random.next() if available to maintain determinism
    const r = random.next ? random.next() : Math.random();
    return value + ((r * range) - variance);
};

// --- STRATEGIES ---

/**
 * Applies a velocity shape (crescendo/decrescendo) over a group of events.
 * Used for "Phrase Shaping".
 * @param {Array} events - The phrase events.
 * @param {string} shape - 'crescendo', 'decrescendo', 'arch'
 */
export const applyPhraseShaping = (events, shape = 'arch') => {
    if (!events || events.length === 0) return events;

    // Sort by time
    // events.sort((a,b) => a.s - b.s); // Assume already sorted or sort locally
    const start = events[0].s;
    const end = events[events.length-1].s;
    const duration = Math.max(0.1, end - start);

    return events.map(ev => {
        const progress = (ev.s - start) / duration; // 0.0 to 1.0
        let modifier = 0;

        if (shape === 'crescendo') {
            modifier = (progress * 0.3); // +0.0 to +0.3
        } else if (shape === 'decrescendo') {
            modifier = (0.3 - (progress * 0.3)); // +0.3 to +0.0
        } else if (shape === 'arch') {
            // Sine wave: sin(0..PI) -> 0..1..0
            modifier = Math.sin(progress * Math.PI) * 0.25;
        }

        return {
            ...ev,
            vel: Math.min(1.0, (ev.vel || 0.7) + modifier)
        };
    });
};

/**
 * Applies Hairpin Phrasing (Phase 6).
 * Analyzes a 4-bar window and applies a breathing dynamic curve.
 */
export const applyHairpinPhrasing = (events, phraseLength = 16.0) => {
    if (!events || events.length === 0) return events;

    // Assume events are chunked or we process them globally.
    // For global list, we must mod the timestamp.

    return events.map(ev => {
        const pos = ev.s % phraseLength; // Position in phrase
        const progress = pos / phraseLength; // 0.0 to 1.0

        // Hairpin: Low -> High (0.6) -> Low
        // Use Sine^2 for smoother curve? Or Sine.
        // Center of breath is around 60% (Golden Ratio approx 0.618)
        // Let's bias the peak slightly later than center.
        // Function: sin(progress * PI) peaks at 0.5.
        // Function: sin(progress^0.8 * PI) peaks later.

        // Intensity Modifier: +/- 0.15 velocity
        const curve = Math.sin(Math.pow(progress, 0.8) * Math.PI);
        const modifier = (curve * 0.3) - 0.15; // Range -0.15 to +0.15

        return {
            ...ev,
            vel: Math.max(0.1, Math.min(1.0, (ev.vel || 0.7) + modifier))
        };
    });
};

/**
 * Applies Agogic Accents to Melody Apex (Phase 6).
 */
export const applyAgogicAccents = (events) => {
    if (!events || events.length === 0) return events;

    // 1. Identify the Apex (Highest Pitch)
    let maxPitch = -1;
    let apexEvent = null;

    // Filter to finding apex in the middle of the phrase (avoiding pickups/cadences) if possible?
    // For simplicity, find absolute max.
    events.forEach(ev => {
        if (ev.d > maxPitch) {
            maxPitch = ev.d;
            apexEvent = ev;
        } else if (ev.d === maxPitch) {
            // If tie, prefer the later one (climax usually later)
            apexEvent = ev;
        }
    });

    if (!apexEvent) return events;

    return events.map(ev => {
        if (ev === apexEvent) {
            return {
                ...ev,
                vel: Math.min(1.0, (ev.vel || 0.7) + 0.15), // Velocity Boost
                s: ev.s + 0.02, // Agogic Delay (20ms approx, abstract time 0.02s)
                agogic: true
            };
        }
        return ev;
    });
};

/**
 * Inserts breath gaps for Wind/Brass instruments.
 * Removes notes that fall within a calculated "breath window" every few bars.
 */
export const applyBreathConstraints = (events, instrumentId, forcePhrasing = false) => {
    if (!instrumentId) return events;
    const id = instrumentId.toLowerCase();

    // Check if Wind or Brass
    const isWind = id.includes('flute') || id.includes('oboe') || id.includes('clarinet') || id.includes('bassoon') || id.includes('wind');
    const isBrass = id.includes('trumpet') || id.includes('horn') || id.includes('trombone') || id.includes('tuba') || id.includes('brass');

    if (!isWind && !isBrass && !forcePhrasing) return events;

    // Breath every 4 bars approx (16 beats)
    // Assume events are sorted
    // Scan time
    const breathingCycle = 16.0;
    const breathDuration = 1.0; // 1 beat rest

    return events.filter(ev => {
        const cyclePos = ev.s % breathingCycle;
        // Breath at end of cycle (last beat)
        // Strict check: Start time >= window start OR End time > window start
        const windowStart = breathingCycle - breathDuration;

        // Remove if it starts in window OR plays through most of it
        if (cyclePos >= windowStart) {
            return false;
        }
        // Also truncate notes that bleed into breath?
        // (Not handling truncation here, just dropping)

        return true;
    });
};

/**
 * Applies vintage pitch drift (Lofi).
 * Adds detune parameter.
 */
export const applyVintageDrift = (events, intensity = 0.5, random) => {
    return events.map(ev => {
        // Slow wobble: Low frequency oscillator
        const time = ev.s;
        const wobble = Math.sin(time * 0.5) * intensity * 20; // +/- 20 cents
        // Fast flutter
        const flutter = (random ? (random.next() - 0.5) : (Math.random() - 0.5)) * intensity * 10;

        return {
            ...ev,
            detune: (ev.detune || 0) + wobble + flutter
        };
    });
};

/**
 * Applies general humanization (Timing Jitter + Velocity Randomization)
 */
export const applyHumanization = (events, amount = 0.5, random, groovePocket = 'center', velocityMultiplier = 1.0, timingMs = null) => {
    let pocketOffset = 0;
    // Use Gaussian for pocket consistency (tightly clustered around the "feel")
    if (groovePocket === 'behind') pocketOffset = fuzz(0.02, 0.005, random); // Lag
    if (groovePocket === 'ahead') pocketOffset = fuzz(-0.01, 0.005, random); // Rush

    return events.map(ev => {
        // --- NON-LINEAR FIX: Gaussian Jitter ---
        // Amount is 0-100 (approx).
        // 50 amount -> stdev should be small (e.g., 0.01s)
        // Timing Jitter
        let tStdev = (amount / 100) * 0.02;

        // Task 1: The "Feel" Injection (Explicit MS Control)
        if (timingMs !== null) {
            tStdev = timingMs / 1000; // Convert MS to Seconds
        }

        const jitter = (random && random.gaussian)
            ? random.gaussian(0, tStdev)
            : ((random && random.next ? random.next() : Math.random()) - 0.5) * tStdev * 2;

        // Velocity Jitter
        const vStdev = (amount / 100) * 0.05 * velocityMultiplier;
        const velJitter = (random && random.gaussian)
            ? random.gaussian(0, vStdev)
            : ((random && random.next ? random.next() : Math.random()) - 0.5) * vStdev * 2;

        return {
            ...ev,
            s: Math.max(0, ev.s + jitter + pocketOffset),
            // Fix: Velocity Floor (Prevent Silence)
            // Ensure velocity never drops below 0.1 even with extreme humanization
            vel: Math.min(1, Math.max(0.1, (ev.vel || 0.7) + velJitter))
        };
    });
};

/**
 * Applies Romantic Rubato (Tempo Drift)
 * Uses a sine wave to simulate accelerando/ritardando over phrases.
 * UPDATED: Added Ritardando Logic at Phrase Ends
 */
export const applyRubato = (events, intensity = 0.5, isRomantic = false) => {
    // Add random variation to intensity per call? No, events are processed in batch.
    // Maybe per event phase drift?
    return events.map(ev => {
        const time = ev.s;
        const phase = (time / 16) * (Math.PI * 2);
        let drift = Math.sin(phase) * (0.15 * intensity); // Fixed amplitude

        // --- NEW: Ritardando at Phrase End ---
        // Every 8 bars (32 beats), slow down significantly
        // Modulo 32
        const posInPhrase = time % 32;
        // Last 2 beats (30-32)
        if (isRomantic && posInPhrase > 30) {
            // Add exponential drag
            // Normalized 0 to 1 over last 2 beats
            const ritProgress = (posInPhrase - 30) / 2;
            drift += (ritProgress * 0.3 * intensity); // Up to 0.3s drag
        }

        return {
            ...ev,
            s: Math.max(0, ev.s + drift)
        };
    });
};

/**
 * Genre-Specific Humanizer Dispatcher
 */
export const applyGenreHumanization = (events, instrument, traits, random, context = {}) => {
    if (!events || events.length === 0) return [];

    // --- CONNECT VECTORS ---
    const motion = (context.config && context.config.vectors) ? context.config.vectors.timbralMotion : 0.5;
    // Ghost Variable Wiring: Energy
    const energy = (context.config && context.config.vectors) ? context.config.vectors.energy : 0.5;

    // --- TRAIT RESOLUTION ---
    const activeTags = [];
    if (traits.character) activeTags.push(traits.character);
    if (instrument.tags) activeTags.push(...instrument.tags);
    if (instrument.capabilities?.traits) {
        Object.values(instrument.capabilities.traits).forEach(v => {
            if (typeof v === 'string') activeTags.push(v);
        });
    }
    // Map specific genre names to tags if needed
    const genreName = traits.name || '';
    if (genreName.includes('Romantic')) activeTags.push('mellow');
    if (genreName.includes('Techno')) activeTags.push('precise');
    if (genreName.includes('Funk')) activeTags.push('loose');

    const modifiers = resolveTraits(activeTags);

    let processed = [...events];
    const isRomantic = genreName.includes('Romantic') || genreName.includes('Expressive');
    const isElectronic = genreName.includes('Techno') || genreName.includes('Trance');
    const isGroove = genreName.includes('Funk') || genreName.includes('Hip Hop');

    // Trait-Based Expression (Phase 4 - Dialed Back)
    let velCap = 1.0;

    // Dynamic Tightness/Chaos
    // Calculate Base Tightness (inverse of Amount)
    // Start with Genre Defaults
    let baseTightness = 0.5;
    if (isElectronic) baseTightness = 0.95;
    else if (isGroove) baseTightness = 0.6;
    else if (isRomantic) baseTightness = 0.3;

    // Apply Modifier Overrides (TraitMap)
    // If modifiers.tightness differs from default 0.5 significantly, use it or average it?
    // resolveTraits defaults to 0.5.
    if (modifiers.tightness !== 0.5) {
        baseTightness = (baseTightness + modifiers.tightness) / 2;
    }

    // Adjust based on Character (Legacy logic replaced/augmented by TraitMap)
    const character = traits.character || 'balanced';

    // Clamp
    baseTightness = Math.max(0.1, Math.min(0.99, baseTightness));

    // Calculate Timing Amount (ms/ticks abstract) based on Tightness
    // Tightness 1.0 -> 0 amount. Tightness 0.0 -> 50 amount.
    // Apply Trait Randomness and Motion Vector
    let timingAmount = (1.0 - baseTightness) * 50 * modifiers.timingRandomness * (0.5 + motion);

    // Dynamic Chaos: Scale jitter with Tension
    const tension = context.tension || 0.5;
    if (character === 'aggressive' || character === 'lazy') {
        timingAmount += (tension * 10);
    } else if (character === 'tight') {
        timingAmount = Math.max(0, timingAmount - (tension * 5));
    }

    // Fuzz the final amount
    timingAmount = fuzz(timingAmount, 2, random);

    // Velocity & Duration Modifiers from Traits
    const durMod = modifiers.duration;
    const velVarMod = modifiers.velocityVariance * (0.5 + energy); // Energy boosts variance
    const velBaseMod = modifiers.velocityBase;

    // Apply Velocity Variance Modifier to the "amount" passed to applyHumanization
    // amount (0-100) controls both Timing and Velocity jitter in applyHumanization.
    // We want to scale velocity jitter specifically.
    // Since applyHumanization takes a single 'amount', we might need to fork it or pass a separate velocity amount.
    // For now, let's inject velocity variance by modifying the event processing loop below if possible,
    // or just let applyHumanization handle it if we trust 'amount'.
    // Wait, 'amount' is derived from 'timingAmount' which is derived from 'tightness'.
    // 'timingAmount' is passed to applyHumanization.
    // But we want independent velocity control via velVarMod.

    // Hack: We can pre-process velocity jitter here using velVarMod before calling applyHumanization?
    // Or simpler: pass velVarMod as a secondary argument to applyHumanization if we refactor it.
    // Let's refactor applyHumanization slightly to accept velocityAmount.

    // 1. DYNAMICS (Grid-Aware)
    // Applied BEFORE timing shifts (Rubato/Groove) to ensure accents land on structural beats.

    // Operation Virtuoso: Apply Dynamic Profile
    let dynamicProfile = traits.dynamicProfile || (instrument.dynamic_profile || 'consistent');

    // Defect Fix: "The Missing Link" - Infer Dynamic Profile from Velocity Strategy if missing
    if (!traits.dynamicProfile && traits.velocityStrategy) {
        if (['dynamic_swell', 'soft_mist', 'crescendo', 'expressive'].includes(traits.velocityStrategy)) {
             dynamicProfile = 'expressive';
        } else if (['fixed_max', 'heavy', 'flat'].includes(traits.velocityStrategy)) {
             dynamicProfile = 'compressed';
        }
    }

    processed = processed.map(e => {
        // Determine Beat Strength (Simple Heuristic)
        // Strong: Beat 0, 2 (in 4/4). Weak: Others.
        // Assumes 4/4.
        const beat = e.s % 4;
        const isStrong = (Math.abs(beat % 2) < 0.1); // Close to integer beat 0, 2

        const profiledVel = calculateVelocity(dynamicProfile, isStrong, random);

        // Blend Factor: How much we trust the profile vs the generated velocity
        let blend = 0.5;
        if (dynamicProfile === 'compressed') blend = 1.0; // Override
        if (dynamicProfile === 'consistent') blend = 0.8;
        if (dynamicProfile === 'expressive') blend = 0.6; // Trust the profile more for Romantic

        const originalVel = e.vel !== undefined ? e.vel : 0.7;
        const newVel = (originalVel * (1 - blend)) + (profiledVel * blend);

        return { ...e, vel: newVel };
    });

    // 2. TIMING
    const pocket = traits.groovePocket || 'center';

    if (isRomantic) {
        processed = applyRubato(processed, 0.8, true);
    }

    // --- PHASE 5: Advanced Groove Engine ---
    // If groove profile exists, use it. Else fallback to basic jitter.
    const configProfile = (context.config && context.config.rhythmProfile && context.config.rhythmProfile.grooveProfile);
    const configOffsets = (context.config && context.config.rhythmProfile && context.config.rhythmProfile.grooveOffsets);
    const grooveProfile = configProfile || traits.grooveProfile || (isGroove ? 'laid_back' : 'straight');

    if (grooveProfile !== 'straight' || configOffsets) {
        processed = applyGroove(processed, grooveProfile, { ...context, grooveOffsets: configOffsets });
        // Reduce random jitter if applying structural groove to avoid chaos
        timingAmount *= 0.5;
    }

    // Task 1: Resolve Timing Profile (MS)
    let timingMs = null;
    if (instrument.humanize_profile) {
        switch (instrument.humanize_profile) {
            case 'locked': timingMs = 0; break;
            case 'tight': timingMs = 5; break;
            case 'loose': timingMs = 15; break;
            case 'expressive': timingMs = 25; break;
            case 'drunk': timingMs = 35; break;
            default: timingMs = 10;
        }
    }

    // Apply Humanization with separated Velocity Variance Multiplier
    processed = applyHumanization(processed, timingAmount, random, pocket, velVarMod, timingMs);

    // Apply Trait Modifiers (Duration & Velocity) with Fuzziness
    if (durMod !== 1.0 || velBaseMod !== 1.0) {
        processed = processed.map(e => {
            // "Nature Hates Straight Lines": Fuzz the static modifiers slightly
            const durFuzz = fuzz(durMod, 0.05, random);
            const velFuzz = fuzz(velBaseMod, 0.05, random);

            return {
                ...e,
                len: Math.max(0.05, e.len * durFuzz),
                vel: Math.min(1.0, (e.vel || 0.7) * velFuzz)
            };
        });
    }

    // --- PHASE 5: Vintage Drift ---
    if (traits.name && (traits.name.includes('Lofi') || traits.name.includes('Analog'))) {
        processed = applyVintageDrift(processed, 0.7, random);
    }


    // Apply Velocity Cap (Expression)
    if (velCap < 1.0) {
        processed = processed.map(e => ({ ...e, vel: Math.min(e.vel || 0.7, velCap) }));
    }

    // 2. GHOST NOTES (Bass/Snare)
    if (instrument && instrument.roles && (instrument.roles.includes('bass') || instrument.roles.includes('percussion') || instrument.roles.includes('snare'))) {
        const allowGhost = !isElectronic;
        if (allowGhost) {
            processed = injectGhostNotes(processed, instrument, random);
        }
    }

    return processed;
};

// --- GENERATORS (Procedural Licks) ---

export const generateWalkingBass = (chord, nextChord, context, barStart, random) => {
    const events = [];
    const root = chord.rootPitchClass;
    const target = nextChord ? nextChord.rootPitchClass : root;

    // 4 Quarter Notes
    // Beat 1: Root
    events.push({ s: barStart, d: root, len: fuzz(0.8, 0.05, random), vel: fuzz(0.9, 0.05, random), notes: [root] });

    // Beat 2: Scale Tone (3rd or 5th) or Octave
    let beat2;
    if (chord.notes && chord.notes.length > 0) {
        beat2 = chord.notes[random ? random.range(0, chord.notes.length) : Math.floor(Math.random() * chord.notes.length)] % 12;
    } else {
        beat2 = (root + 7) % 12; // Default to 5th
    }
    events.push({ s: barStart + 1, d: beat2, len: fuzz(0.8, 0.05, random), vel: fuzz(0.8, 0.05, random), notes: [beat2] });

    // Beat 3: 5th or Octave
    const beat3 = (root + 7) % 12;
    events.push({ s: barStart + 2, d: beat3, len: fuzz(0.8, 0.05, random), vel: fuzz(0.85, 0.05, random), notes: [beat3] });

    // Beat 4: Approach Note (Chromatic or Diatonic to Target)
    let beat4 = (target - 1 + 12) % 12;
    const r1 = random ? random.next() : Math.random();
    const r2 = random ? random.next() : Math.random();

    if (r1 > 0.5) beat4 = (target + 1) % 12;
    if (r2 > 0.7) beat4 = (target + 7) % 12;

    events.push({ s: barStart + 3, d: beat4, len: fuzz(0.8, 0.05, random), vel: fuzz(0.75, 0.05, random), notes: [beat4] });

    return events;
};

export const generateGuitarLick = (chord, time, duration, random) => {
    // Pentatonic run
    const events = [];
    const root = chord.rootPitchClass;
    const intervals = [0, 3, 5, 7, 10];
    const baseOctave = 60;

    for (let i = 0; i < 4; i++) {
        const idx = random ? random.range(0, intervals.length) : Math.floor(Math.random() * intervals.length);
        const interval = intervals[idx];
        const pitch = root + interval + baseOctave;
        events.push({
            s: time + (i * 0.25),
            d: pitch,
            len: fuzz(0.2, 0.02, random),
            vel: fuzz(0.9, 0.05, random),
            articulation: 'hammer_on'
        });
    }
    return events;
};

export const injectGhostNotes = (events, instrument, random) => {
    const ghosts = [];
    events.forEach(ev => {
        const r = random ? random.next() : Math.random();
        if (r < fuzz(0.15, 0.05, random) && ev.s > 0.25) {
            ghosts.push({
                s: ev.s - fuzz(0.125, 0.01, random),
                d: ev.d,
                len: fuzz(0.1, 0.02, random),
                vel: fuzz(0.3, 0.05, random),
                ghost: true,
                role: ev.role,
                instrumentId: instrument.id
            });
        }
    });
    return [...events, ...ghosts].sort((a,b) => a.s - b.s);
};
